import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  showPageNumbers?: boolean;
  maxVisiblePages?: number;
  className?: string;
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  showPageSizeSelector?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  showPageNumbers = true,
  maxVisiblePages = 5,
  className = "",
  pageSize = 10,
  onPageSizeChange,
  showPageSizeSelector = true,
}) => {
  // Don't render if no pages
  if (totalPages < 1) return null;

  const getVisiblePages = () => {
    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxVisiblePages - 1);

    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const visiblePages = getVisiblePages();

  const buttonBaseClasses =
    "px-3 py-2 rounded-xl font-bold transition-all duration-300 border-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100";
  const activeButtonClasses =
    "bg-gradient-to-br from-blue-500 to-indigo-600 border-blue-600 text-white shadow-lg";
  const inactiveButtonClasses =
    "bg-white border-slate-200 text-slate-700 hover:shadow-lg hover:scale-105 hover:border-blue-300";

  return (
    <div className={`flex items-center justify-between gap-4 ${className}`}>
      {/* Results info */}
      <div className="text-sm font-bold text-slate-600">
        Page {currentPage} of {totalPages}
      </div>

      {/* Pagination controls */}
      <div className="flex items-center gap-2">
        {/* First page */}
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className={`${buttonBaseClasses} ${inactiveButtonClasses}`}
          title="First page"
        >
          <ChevronsLeft className="h-4 w-4" />
        </button>

        {/* Previous page */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`${buttonBaseClasses} ${inactiveButtonClasses}`}
          title="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {/* Page numbers */}
        {showPageNumbers && (
          <div className="flex items-center gap-1">
            {visiblePages.map((page) => (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`${buttonBaseClasses} ${
                  page === currentPage
                    ? activeButtonClasses
                    : inactiveButtonClasses
                } min-w-[44px]`}
              >
                {page}
              </button>
            ))}
          </div>
        )}

        {/* Next page */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`${buttonBaseClasses} ${inactiveButtonClasses}`}
          title="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>

        {/* Last page */}
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className={`${buttonBaseClasses} ${inactiveButtonClasses}`}
          title="Last page"
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
      </div>

      {/* Page size selector */}
      {showPageSizeSelector && onPageSizeChange && (
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-slate-600">Show:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="px-3 py-2 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-bold text-slate-700 focus:border-blue-400 focus:outline-none transition-all"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      )}
    </div>
  );
};

export default Pagination;
